import React, { useEffect, useMemo, useState } from 'react'
import { Form, Formik } from 'formik'
import { Flex } from 'ui/acro'
import { axios } from 'api'
import * as Yup from 'yup'
import styled from 'styled-components'
import AccountLocationError from './common/AccountLocationError'
import { SubscriptionDisclaimer } from 'ui/bend'
import { FloatingLabelPassword, FloatingLabelInput } from 'ui/acro'
import { MarketingDislaimer } from 'ui/chandra'
import { Button, Feedback } from 'ui/bend'
import {
  setCookie,
  HAS_ALO_AUTH_TOKEN_STATE_COOKIE,
  redirect_to_with_cookie_creation
} from 'components_v2/common'
import { sendAccessTokenToApp } from '../../../SignIn/AloAccess/utils'
import { useGetCountryCode } from 'hooks'
import { sign_up_event, sign_up_initiated_event } from 'gtm/pushEvents'
import { sign_up_braze_event } from 'braze/brazeEvents'
import { useUserContext } from 'context'

const FormComponent = styled(Form)`
  .submit-button {
    font-family: 'Proxima-Nova';
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.48px;
    text-transform: uppercase;
    padding: 0px 20px !important;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    width: 100%;
    color: #fff;
    margin-top: 36px;
    background: #000;
    @media (max-width: 768px) {
      padding: 0px !important;
    }
  }
`

const JoinNow = styled.div`
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 135%;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 22px 10px 10px 10px;
  border-bottom: 4px solid #000;
  margin-bottom: 24px;
`

const GiftText = styled.div`
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0.308px;
`

const DisclaimerWrapper = styled.div`
  margin-top: 40px;
`

const ActionHandler = styled.div`
  font-size: 14px;
  font-weight: 300;
  line-height: 24px;
  letter-spacing: 0.352px;
  margin-left: 1.5rem;
  font-family: 'Proxima-Nova';
`

const Link = styled('a')(({ isSettingsPage }) => ({
  color: '#000',
  fontFamily: 'Proxima-Nova',
  fontSize: '14px',
  fontWeight: isSettingsPage ? 600 : 400,
  lineHeight: '18px',
  letterSpacing: '0.308px',
  textDecoration: 'underline !important',
  '&:visited': {
    color: '#000',
    textDecoration: 'underline !important'
  },
  '&:focus': {
    color: '#000',
    textDecoration: 'underline !important'
  }
}))
const formatPhoneNumber = (value) => {
  const cleaned = value.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)
  if (match) {
    const [, area, prefix, line] = match
    if (line) return `(${area}) ${prefix}-${line}`
    if (prefix) return `(${area}) ${prefix}`
    if (area) return `(${area}`
  }
  return ''
}

const formatMaskedDate = (value) => {
  const digits = value.replace(/\D/g, '')
  let result = ['_', '_', '/', '_', '_']
  let digitIndex = 0
  for (let i = 0; i <= digits.length; i++) {
    if (/\d/.test(result[i]) || (result[i] === '_' && i !== 2)) {
      if (digitIndex < digits.length) {
        result[i] = digits[digitIndex++]
      }
    }
  }
  const joinResult = result.join('')
  return joinResult === '__/__' ? '' : joinResult
}

const phoneRegExp = /^(\+?\d{1,3}[- ]?)?\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}$/
const mmddRegex = /^\d{2}\/\d{2}$/

const schema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  email: Yup.string()
    .required('Email is required')
    .email('Invalid email format'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  phonenumber: Yup.string().matches(phoneRegExp, 'Phone number is not valid'),
  dob: Yup.string().matches(mmddRegex, 'Date must be in MM/DD format')
})

const isVersionLessThan = (version, target) => {
  if (!version) {
    return false
  }

  const v1 = version.split('.').map(Number)
  const v2 = target.split('.').map(Number)

  const length = Math.max(v1.length, v2.length)

  for (let i = 0; i < length; i++) {
    const a = v1[i] ?? 0
    const b = v2[i] ?? 0

    if (a < b) return true
    if (a > b) return false
  }

  return false
}

const SignUpForm = ({
  setCustomerEmail,
  signUpCallbackUrl,
  isSettingsPage,
  verifyLink = '/verify-alo',
  iosAppClientId,
  iosAppVersion,
  iosAppApiVersion,
  setSubmitData = () => {},
  iosAppScope
}) => {
  const { user: currentUser } = useUserContext()
  const country = useGetCountryCode()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [signupFailedError, setSignupFailedError] = useState(false)
  const [signupFailedErrorMessage, setSignupFailedErrorMessage] = useState({
    message: '',
    action: ''
  })
  const [error, setError] = useState()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const urlParams = new URLSearchParams(window.location.search)
  const pageParams =
    urlParams.get('page') === 'membership' ? '&page=membership' : ''
  const sign_up_initiated =
    urlParams.get('sign_up_initiated')?.toString() === 'true'
  const location = urlParams.get('location')?.toString() || 'homepage'

  useEffect(() => {
    if (sign_up_initiated) {
      sign_up_initiated_event('email', location)
      window.history.pushState({}, '', '/signup')
    }
  }, [sign_up_initiated, location])

  const iosAppQueryParams = Boolean(iosAppClientId)
    ? {
        version: iosAppVersion,
        os: 'ios',
        api_version: iosAppApiVersion,
        client_id: iosAppClientId,
        scope: iosAppScope
      }
    : {}

  const actionLinks = {
    link: {
      href: `${verifyLink}`,
      text: 'Link your ALO account',
      suffix: ' to AWC to proceed.'
    },
    signin: {
      href: '/signin',
      text: 'Login',
      suffix: ' to your account to continue.'
    }
  }

  const checkWebView = () => {
    if (!Boolean(iosAppClientId) || pageParams || currentUser) {
      return true
    }

    window.webkit?.messageHandlers?.tokenHandler?.postMessage({
      action: 'login'
    })

    return false
  }

  const aloSignUp = async (data) => {
    setIsSubmitting(true)
    setSignupFailedError(false)
    let cancelIsSubmitting = false
    try {
      const res = await axios.post('/sso/signup_to_alo', {
        ...data,
        ...iosAppQueryParams,
        redirect_to: signUpCallbackUrl
      })
      const {
        signup = 'failure',
        redirectUrl = '/',
        showConcernToLink = false,
        existingUser = false,
        error = '',
        accessTokens,
        linkedUser,
        user = {},
        tier = ''
      } = res.data

      if (signup === 'successful') {
        if (!showConcernToLink) {
          sign_up_event(tier, existingUser, user)
          sign_up_braze_event(tier, existingUser, user)
          setCookie('alo_access_session_established', 'true', 1 / 24)
        }
        if (existingUser) {
          if (showConcernToLink) {
            const otpRes = await axios.post('/sso/send_alo_link_otp', {
              ...data,
              ...iosAppQueryParams
            })
            const { message } = otpRes.data
            if (message === 'OTP sent successfully') {
              const { email = '' } = data

              cancelIsSubmitting = true
              setSubmitData({
                ...data,
                ...iosAppQueryParams,
                redirect_to: signUpCallbackUrl
              })
              setCustomerEmail(email)
              setError(false)
            } else if (
              message === 'Too many requests. Please try again later.'
            ) {
              setError(message)
            }
          } else {
            sendAccessTokenToApp({
              email: data?.email,
              accessTokens,
              linkedUser,
              user,
              isSignUpWebViewSuccess: true
            })
            localStorage.setItem('signupSuccess', 'true')
            if (!Boolean(iosAppClientId)) {
              redirect_to_with_cookie_creation(
                HAS_ALO_AUTH_TOKEN_STATE_COOKIE ? '/' : redirectUrl
              )
            }
          }
        } else {
          sendAccessTokenToApp({
            email: data?.email,
            accessTokens,
            linkedUser,
            user,
            isSignUpWebViewSuccess: true
          })
          localStorage.setItem('signupSuccess', 'true')
          if (!Boolean(iosAppClientId)) {
            redirect_to_with_cookie_creation(
              HAS_ALO_AUTH_TOKEN_STATE_COOKIE
                ? '/surveys/onboarding'
                : redirectUrl
            )
          }
        }
      } else {
        setSignupFailedErrorMessage({
          message: error,
          action: res?.data?.action
        })
        setSignupFailedError(true)
        sendAccessTokenToApp({
          email: data?.email,
          error,
          isSignUpWebViewSuccess: false
        })
      }
    } catch (error) {
      console.log(error)
      setSignupFailedErrorMessage({
        message:
          error?.response?.data?.error ||
          'Something went Wrong. Please try again.',
        action: error.response?.data?.action
      })
      setSignupFailedError(true)
      sendAccessTokenToApp({
        email: data?.email,
        error,
        isSignUpWebViewSuccess: false
      })
    } finally {
      if (!cancelIsSubmitting) {
        setIsSubmitting(false)
      }
    }
  }

  const handleKeyDown = (e, currentValue, setFieldValue) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const digits = currentValue.replace(/\D/g, '').slice(0, 4)
      const shortened = digits.slice(0, -1)
      const formatted = formatMaskedDate(shortened)
      setFieldValue('dob', formatted)
    }
  }

  const showActionButton = useMemo(() => {
    const action = signupFailedErrorMessage?.action
    const linkConfig = ['signin', 'link'].includes(action)

    if (!linkConfig) return false
    if (action === 'signin' && isSettingsPage) return false
    if (action === 'signin' && Boolean(iosAppClientId)) return false

    return true
  }, [signupFailedErrorMessage?.action, isSettingsPage, iosAppClientId])

  return (
    <>
      <Formik
        enableReinitialize
        validationSchema={schema}
        initialValues={{
          firstName: currentUser?.firstName || '',
          lastName: currentUser?.lastName || '',
          email: currentUser?.email || '',
          password: '',
          phonenumber: '',
          dob: '',
          consent_marketing_email: country === 'US' ? true : false
        }}
        onSubmit={aloSignUp}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit
        }) => (
          <FormComponent>
            <JoinNow>JOIN Now</JoinNow>
            <Flex justifyContent='space-between'>
              <FloatingLabelInput
                name='firstName'
                autoComplete='given-name'
                handleChange={handleChange}
                errors={errors}
                touched={touched}
                placeHolder='First Name'
                onBlur={handleBlur}
                width='47%'
              />

              <FloatingLabelInput
                name='lastName'
                autoComplete='family-name'
                handleChange={handleChange}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeHolder='Last Name'
                width='47%'
              />
            </Flex>
            <FloatingLabelInput
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              name='email'
              placeHolder='Email Address'
              isLabelRequired={false}
              onBlur={handleBlur}
            />
            <FloatingLabelPassword
              name='password'
              showValidations={false}
              values={values}
              errors={errors}
              touched={touched}
              onBlur={handleBlur}
              handleChange={handleChange}
              placeHolder='Password'
            />
            {country === 'US' && (
              <>
                <FloatingLabelInput
                  name='phonenumber'
                  handleChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value)
                    setFieldValue('phonenumber', formatted)
                  }}
                  errors={errors}
                  touched={touched}
                  isToolTip={true}
                  onBlur={handleBlur}
                  toolTipText='Enables faster account lookup in-store.'
                  placeHolder='Phone Number (optional)'
                  maxLength={14}
                />

                <GiftText>Enter your birthday to get a free gift</GiftText>

                <FloatingLabelInput
                  name='dob'
                  handleChange={(e) => {
                    const formatted = formatMaskedDate(e.target.value)
                    setFieldValue('dob', formatted)
                  }}
                  onKeyDown={(e) => handleKeyDown(e, values.dob, setFieldValue)}
                  errors={errors}
                  touched={touched}
                  toolTipText='Get a special Alo treat on your birthday!'
                  placeHolder='MM/DD (optional)'
                  isToolTip={true}
                  onBlur={handleBlur}
                />
              </>
            )}
            <DisclaimerWrapper>
              <MarketingDislaimer
                mb={'16px'}
                checked={values.consent_marketing_email}
                consentName={'consent_marketing_email'}
              />
              <SubscriptionDisclaimer
                cta={'Join Now'}
                hideTerms={false}
                mt={'8px'}
                hideSignInDisclimer={true}
                style={{ letterSpacing: 'normal' }}
              />
            </DisclaimerWrapper>
            {signupFailedError && (
              <>
                <AccountLocationError
                  {...(['link', 'signin'].includes(
                    signupFailedErrorMessage.action
                  ) && {
                    style: {
                      marginBottom: 0
                    }
                  })}
                >
                  {signupFailedErrorMessage?.message}
                </AccountLocationError>
                {showActionButton &&
                  (Boolean(iosAppClientId) &&
                  isVersionLessThan(iosAppVersion, '5.60.0') ? (
                    <ActionHandler>
                      <Link
                        href={
                          actionLinks[signupFailedErrorMessage?.action].href
                        }
                      >
                        {actionLinks[signupFailedErrorMessage?.action].text}
                      </Link>
                      {actionLinks[signupFailedErrorMessage?.action].suffix}
                    </ActionHandler>
                  ) : (
                    <ActionHandler>
                      <Link
                        href={
                          pageParams || currentUser
                            ? `${verifyLink}${pageParams}`
                            : '/signin'
                        }
                        onClick={checkWebView}
                      >
                        {pageParams || currentUser ? 'Verify' : 'Sign In'}
                      </Link>{' '}
                      {pageParams || currentUser ? '' : 'to '}
                      your account to continue.
                    </ActionHandler>
                  ))}
              </>
            )}
            <Button
              width='100%'
              mt='48px'
              mb='28px'
              style={{ fontSize: '1.125rem', fontWeight: 700 }}
              type='submit'
              processing={isSubmitting}
              id='alo_sign_up_button'
              className='submit-button'
            >
              JOIN NOW
            </Button>
          </FormComponent>
        )}
      </Formik>
      {error && (
        <Feedback
          className='error'
          style={{ textAlign: 'center', marginBottom: '8px' }}
        >
          {error}
        </Feedback>
      )}
    </>
  )
}
SignUpForm.propTypes = {}

export default SignUpForm
